import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

export function Field({ f, value, onChange, testPrefix }) {
  const id = `${testPrefix}-${f.name}-${f.type === "select" ? "select" : "input"}`;
  const common = { id, "data-testid": id, name: f.name, required: f.required, placeholder: f.placeholder,
    value: value ?? "", onChange: (e) => onChange(f.name, e.target.value) };
  return (
    <div className={f.col === 2 ? "md:col-span-2" : ""}>
      <label htmlFor={id} className="block text-xs font-semibold text-slate-400 mb-1.5 tracking-wide">{f.label}{f.required && <span className="gold-text"> *</span>}</label>
      {f.type === "textarea" ? <textarea {...common} className="field-area" />
        : f.type === "select" ? (
          <select {...common} className="field">
            {!f.required && <option value="">—</option>}
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : <input {...common} type={f.type || "text"} step={f.step} min={f.min} className="field" />}
      {f.hint && <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial = {}, onSubmit, submitLabel = "Save", testPrefix = "form", wide, extra, header }) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setValues(initial); }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const out = {};
      fields.forEach((f) => { let v = values[f.name]; if (v === "" || v === undefined) v = null; else if (f.type === "number") v = Number(v); out[f.name] = v; });
      await onSubmit(out);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`panel ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`} data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{title}</DialogTitle>
          {description && <DialogDescription className="text-slate-400">{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {header && <div className="md:col-span-2">{header(values, (patch) => setValues((s) => ({ ...s, ...patch })))}</div>}
          {extra && <div className="md:col-span-2 empty:hidden">{extra(values)}</div>}
          {fields.map((f) => <Field key={f.name} f={f} value={values[f.name]} onChange={(n, v) => setValues((s) => ({ ...s, [n]: v }))} testPrefix={testPrefix} />)}
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${testPrefix}-cancel-button`}>Cancel</Button>
            <Button type="submit" disabled={busy} className="btn-gold" data-testid={`${testPrefix}-submit-button`}>{busy ? "Saving…" : submitLabel}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
